import { SnackBar, SpinnyCat, Link } from "@ui-lib/index.tsx";
import { LeftPanel, RightPanel, QrButton } from "./indexCreativeViewer";
import { Subheader } from "../../composites/Subheader/Subheader";
import DontCry from "../../composites/DontCry/DontCry";
import Share from "@components/UI/Share/Share";
import Favourite from "@components/UI/Favourite/Favourite";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router";
import { userMetadata } from "../../../data/userMetadata.js";
import DemoControls from "../../components/DemoControls/DemoControls";
import GUI from "../../components/GUI/GUI";
import Graph from "../../composites/Graph/Graph";
import { CreativeStore } from "../../store/CreativeStore";
import { GUIStore } from "../../store/GUIStore";
import { MetricStore } from "../../store/MetricStore";
import { UIStore } from "../../store/UIStore";
import { useLocation } from "react-router-dom";
import useStudioBuildID from "../../hooks/useStudioBuildID";
import { getPrebuiltAdElement } from "@gumgum-inc/creative-renderer";
import { generatePreviewerURL } from "../../util/creativeUtil";
import { withAlertsRender } from "../Portals";
import { logAnalytics } from "../../util/analyticsUtil";
import { Center, HStack, VStack, Text, Button } from "@chakra-ui/react";
import { isNewStudio } from "../../util/idTypeChecker";
import LegacyPreviewer from "./LegacyPreviewer";
import StudioPreviewer from "./StudioPreviewer";

const SnackBarAlert = withAlertsRender(SnackBar);

const CreativeViewer = (props) => {
  // LOCAL STATES
  const [qrExpand, setQrExpand] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [creativeScrolled, setCreativeScrolled] = useState(false);

  const [prebuiltAdElement, setPrebuiltAdElement] = useState(null);

  // GLOBAL STATES
  const userInfo = UIStore((state) => state.userInfo);
  const setMissingDemoCreative = UIStore(
    (state) => state.setMissingDemoCreative
  );
  const setIframeVideoProgress = UIStore(
    (state) => state.setIframeVideoProgress
  );

  const creatives = MetricStore((state) => state.creatives);
  const attentionTime = MetricStore((state) => state.attentionTime);

  const buildID = CreativeStore((state) => state.buildID);
  const sequences = CreativeStore((state) => state.sequences);
  const scrollMode = CreativeStore((state) => state.scrollMode);
  const dwellTimes = CreativeStore((state) => state.dwellTimes);
  const scriptVersion = CreativeStore((state) => state.scriptVersion);
  const scrubBaseline = CreativeStore((state) => state.scrubBaseline);
  const dwellTimeBaseline = CreativeStore((state) => state.dwellTimeBaseline);
  const sessionSize = CreativeStore((state) => state.sessionSize);
  const videoSequences = CreativeStore((state) => state.videoSequences);
  const setFormatObject = CreativeStore((state) => state.setFormatObject);
  const setIframeVideo = CreativeStore((state) => state.setIframeVideo);
  const setBuildID = CreativeStore((state) => state.setBuildID);
  const clearBuildID = CreativeStore((state) => state.clearBuildID);
  const brandNames = CreativeStore((state) => state.brandNames);
  const campaignNames = CreativeStore((state) => state.campaignNames);
  const creativeNames = CreativeStore((state) => state.creativeNames);
  const usingAggregatedData = CreativeStore(
    (state) => state.usingAggregatedData
  );
  const errorLoadingCreative = CreativeStore(
    (state) => state.errorLoadingCreative
  );
  let creativeFormat = CreativeStore((state) => state.format); // overwriten if demo mode
  let vertical = CreativeStore((state) => state.vertical);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const previewID = queryParams.get("preview");

  const iframeSrcDoc = CreativeStore((state) => state.iframeSrcDoc);
  const fetchIframeSourceDoc = CreativeStore(
    (state) => state.fetchIframeSourceDoc
  );
  const clearIframe = CreativeStore((state) => state.clearIframe);
  const detachDemoDatabase = CreativeStore((state) => state.detachDemoDatabase);
  const loadCreativeSequences = CreativeStore(
    (state) => state.loadCreativeSequences
  );
  const clearSequences = CreativeStore((state) => state.clearSequences);
  const creativeScrollProgress = CreativeStore(
    (state) => state.creativeScrollProgress
  );
  const setCreativeScrollProgress = CreativeStore(
    (state) => state.setCreativeScrollProgress
  );
  const containsVideo = CreativeStore((state) => state.containsVideo);
  const domainsList = CreativeStore((state) => state.domainsList);
  const defaultBaseline = CreativeStore((state) => state.defaultBaseline);
  const setBaseline = CreativeStore((state) => state.setBaseline);

  const minSequenceLength = GUIStore((state) => state.minSequenceLength);
  const triggerType = GUIStore((state) => state.triggerType);
  const domainWhiteList = GUIStore((state) => state.domainWhiteList);
  const creativeBlacklist = UIStore((state) => state.creativeBlacklist);
  // Hardcoding the creativeFormat for now, since the demo creative
  // doesn't have one.
  if (props.demoMode) creativeFormat = "hang-time";

  // LIFE CYCLE EVENTS

  // here we're forcing the buildID to always be the parent studio buildID, even if
  // the user is requesting via an adBuyId or creativeId
  const checkedBuildID = useStudioBuildID(props.match.params.creativeID);

  // Set buildID state when props changed
  useEffect(() => {
    if (props.demoMode && userInfo.loggedIn) {
      logAnalytics.demoViewed();
      const matchingUser = userMetadata.filter((userMeta) => {
        return (
          props.userEmail
            .toLowerCase()
            .includes(userMeta.email.toLowerCase()) ||
          userMeta.email.toLowerCase().includes(props.userEmail.toLowerCase())
        );
      });
      if (matchingUser.length) {
        const demoCreativeID = matchingUser[0].demoCreativeID;
        setBuildID(demoCreativeID);
      } else {
        logAnalytics.demoNoCreative();
        setMissingDemoCreative(true);
      }
    } else if (checkedBuildID) {
      setBuildID(checkedBuildID);
    }
  }, [
    props.mode,
    props.demoMode,
    props.match.params.creativeID,
    props.userEmail,
    userInfo.loggedIn,
    checkedBuildID,
  ]);

  // fetch iframe source doc when build id changes
  useEffect(() => {
    if (buildID) {
      // set title to buildID
      document.title = `CAT report - ${buildID}`;

      // Analytics - log creative viewed
      logAnalytics.creativeViewed(buildID);

      // Determine which previewer flow to use:
      // - If preview ID exists, its type decides the flow
      // - Otherwise, fall back to main buildID type
      const legacyIDToLoad = previewID && !isNewStudio(previewID) ? previewID
        : !previewID && !isNewStudio(buildID) ? buildID
        : null;

      if (legacyIDToLoad) {
        try {
          fetchIframeSourceDoc(legacyIDToLoad);
        } catch (error) {
          setErrorMessage(error);
        }
      }

      return () => {
        document.title = "Creative Attention Tracker";
      };
    }
  }, [buildID, previewID]);

  useEffect(() => {
    // Determine which ID to use for the new studio flow:
    // - If preview ID exists and is new studio, use it
    // - If no preview ID and buildID is new studio, use buildID
    // - If preview ID is legacy, skip new studio entirely (legacy flow handles it)
    const newStudioIDToLoad = previewID && isNewStudio(previewID) ? previewID
      : !previewID && isNewStudio(buildID) ? buildID
      : null;

    if (!newStudioIDToLoad) {
      return;
    }

    const effectiveBuildID = newStudioIDToLoad;

    const url = `https://create.gumgum.com/api/get-ad-tag/?id=${effectiveBuildID}`;

    const fetchData = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const adElement = await getPrebuiltAdElement({
          creativeId: effectiveBuildID,
          version: data.version,
          productType: data.productName,
          unitType: data.unitName,
          wrapWithPageContent: true,
          userAgent:
            "Mozilla/5.0 (Linux; Android 9; SM-G955U) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/114.0.0.0 Mobile Safari/537.36",
        });

        setPrebuiltAdElement(adElement);
      } catch (err) {
        console.error("Failed to fetch ad tag:", err);
      }
    };

    fetchData();
  }, [buildID, previewID]);

  // load sequences when setBuildID or minSequenceLength changes
  useEffect(() => {
    if (buildID) {
      try {
        loadCreativeSequences(
          buildID,
          minSequenceLength,
          triggerType,
          [],
          props.demoMode && userInfo.loggedIn
        );
      } catch (error) {
        setErrorMessage(error);
      }
    }
  }, [
    minSequenceLength,
    props.demoMode,
    userInfo.loggedIn,
    buildID,
    triggerType,
  ]);

  useEffect(() => {
    if (buildID) loadCreativeSequences(buildID, 1, "scrub", domainWhiteList);
  }, [domainWhiteList]);

  const baselineOverride = GUIStore((state) => state.baselineOverride);
  useEffect(() => {
    setBaseline(baselineOverride);
  }, [baselineOverride]);

  // general unmount clear useless states preventing pollution. for real!
  useEffect(() => {
    return () => {
      if (props.demoMode) detachDemoDatabase(buildID);
      clearIframe();
      clearSequences();
      clearBuildID();
    };
  }, []);

  // Any way to move these into a seperate file to not clog up this one?
  // custom hooks?
  // analytics - scrolled
  useEffect(() => {
    if (creativeScrollProgress > 50 && creativeScrolled === false) {
      setCreativeScrolled(true);
      logAnalytics.creativePreviewScrubed(buildID);
    }
  }, [creativeScrollProgress]);

  // analytics - viewed
  useEffect(() => {
    if (sessionSize > 0) {
      logAnalytics.creativeSessionsLoaded(
        buildID,
        false,
        sessionSize,
        creativeFormat ?? "unknown"
      );
    }
  }, [sessionSize]);

  // alerts and errors
  const zeroMetricError = attentionTime === 0;

  // -----------------------------------------------------
  // should be in presentation mode?
  const isPresentationMode = queryParams.get("layoutMode") === "presentation";
  // this is the URL param that is passed thorugh when gif-generator
  // is viewing the page to create a gif for the slide deck

  // check if we're manually hiding this build id
  const creativeIsBlacklisted = creativeBlacklist.includes(buildID);

  // if we are manually hiding it, and we're not in presentation mode, show a 404
  const show404 = creativeIsBlacklisted && !isPresentationMode;

  // 404 page
  if (show404) {
    return (
      <Center minH="90vh" w="100vw" as={VStack}>
        <Text fontSize="18px">
          This creative isn't available to view at the moment.
        </Text>
        <Button onClick={() => props.history.push("/")} mt={4}>
          Go back
        </Button>
      </Center>
    );
  }
  // -----------------------------------------------------

  return (
    <VStack
      width="100vw"
      height={isPresentationMode ? "100vh" : "calc(100vh - 50px)"}
      pt={isPresentationMode ? "0" : "50px"}
    >
      <Subheader
        display={isPresentationMode ? "none" : "flex"}
        title={
          <Text type="body02" as="span">
            Cat Report &nbsp;
            {creativeFormat && (
              <Link
                href={generatePreviewerURL(buildID, creativeFormat) || "#"}
                title="Preview this ad in a new tab"
                maxWidth="200px"
                target="_blank"
              >
                {creativeNames}
              </Link>
            )}
          </Text>
        }
        sessionSize={sessionSize}
        minSessions={5000}
      >
        <QrButton buildId={buildID} />
        <Share
          description={"Share a link to this CAT report"}
          content={"Share"}
          buildIDs={[buildID]}
          url={window.location.href}
          creative={{
            id: buildID,
            containsVideo,
          }}
        />
        {userInfo.loggedIn && (
          <Favourite buildID={buildID} userID={userInfo.id} />
        )}
      </Subheader>
      <HStack
        id="creative-viewer-container"
        maxWidth={isPresentationMode ? "100%" : "var(--maxScreenWidth)"}
        alignItems="flex-start"
        width="100%"
        height="100%"
      >
        {!errorLoadingCreative && (
          <LeftPanel
            p={isPresentationMode ? 0 : undefined}
            borderRight={
              isPresentationMode ? "none" : "1px solid var(--colors-neutral-4)"
            }
          >
            {/* Legacy Previewer - show when preview ID is legacy, or no preview and buildID is legacy */}
            {creativeFormat && buildID && ((previewID && !isNewStudio(previewID)) || (!previewID && !isNewStudio(buildID))) && (
              <LegacyPreviewer
                buildID={buildID}
                iframeSrcDoc={iframeSrcDoc}
                creativeFormat={creativeFormat}
                sessionSize={sessionSize}
                attentionTime={attentionTime}
                scriptVersion={scriptVersion}
                creativeNames={creativeNames}
                brandNames={brandNames}
                campaignNames={campaignNames}
                scrollMode={scrollMode}
                containsVideo={containsVideo}
                setCreativeScrollProgress={setCreativeScrollProgress}
                setFormatObject={setFormatObject}
                setIframeVideo={setIframeVideo}
                setIframeVideoProgress={setIframeVideoProgress}
              />
            )}
            {/* New Studio Previewer - show when preview ID is new studio, or no preview and buildID is new studio */}
            {creativeFormat &&
              buildID &&
              ((previewID && isNewStudio(previewID)) || (!previewID && isNewStudio(buildID))) &&
              prebuiltAdElement && (
                <StudioPreviewer
                  buildID={(previewID && isNewStudio(previewID)) ? previewID : buildID}
                  prebuiltAdElement={prebuiltAdElement}
                  creativeFormat={creativeFormat}
                  sessionSize={sessionSize}
                  attentionTime={attentionTime}
                  scriptVersion={scriptVersion}
                  creativeNames={creativeNames}
                  brandNames={brandNames}
                  campaignNames={campaignNames}
                  setCreativeScrollProgress={setCreativeScrollProgress}
                  containsVideo={containsVideo}
                  setIframeVideo={setIframeVideo}
                  setIframeVideoProgress={setIframeVideoProgress}
                />
              )}
          </LeftPanel>
        )}
        <RightPanel>
          {props.demoMode || creativeFormat ? (
            <>
              <Graph
                creative={{
                  buildID,
                  sequences,
                  scrollMode,
                  creatives,
                  dwellTimes,
                  scrubBaseline,
                  dwellTimeBaseline,
                  sessionSize,
                  creativeFormat,
                  videoSequences,
                  containsVideo,
                  vertical,
                  usingAggregatedData,
                }}
                adProgress={creativeScrollProgress}
                showMetric={true}
                demoMode={props.demoMode}
              />

              {props.demoMode && <DemoControls />}
            </>
          ) : errorLoadingCreative ? (
            <DontCry
              title="Error Loading Report"
              subtitle="We're sorry, the report you requested could not be found"
              message="Please check the ID and try again. Contact support if the problem persists."
            />
          ) : (
            <SpinnyCat size="s" />
          )}
        </RightPanel>
      </HStack>
      {userInfo.loggedIn && (
        <GUI
          display={isPresentationMode ? "none" : "flex"}
          {...{
            domainsList,
            dwellTimes,
            defaultBaseline,
          }}
        />
      )}
      {/* ---- ERRORS ----- */}
      {!!errorMessage && !props.demoMode && (
        <SnackBarAlert
          title={
            errorMessage
              ? `${errorMessage}`
              : `We couldn't find your chart at this time.`
          }
          status="error"
        />
      )}
      {!!props.demoMode &&
        sessionSize <= 1 &&
        !userInfo.missingDemoCreative && (
          <SnackBarAlert
            title={
              <>
                This demo creative doesn't have any recorded sessions.
                Scan&nbsp;
                <Link onClick={() => setQrExpand(!qrExpand)}>the QR code</Link>
                &nbsp; to start gathering data ↗
              </>
            }
            status="warning"
          />
        )}
      {!!props.demoMode &&
        !!userInfo.missingDemoCreative &&
        !!userInfo.loggedIn && (
          <SnackBarAlert
            title="Pssstt... you should ask Andrea to setup a demo creative for your account."
            delayToClose={8000}
            status="warning"
          />
        )}
      {zeroMetricError && (
        <SnackBarAlert
          title={
            <>
              We're having some issues fetching the metrics for this creative.
              Sorry! Please check back a little later.
            </>
          }
          status="warning"
        />
      )}
    </VStack>
  );
};

const CreativeViewerWithRouter = withRouter(CreativeViewer);

export default CreativeViewerWithRouter;
